import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Card, CardContent, Grid, makeStyles, Typography } from "@material-ui/core";

import { PAGES } from "..";
import { theme } from "../../theme";
import { COLORS } from "../../theme/colors";
import { USER_ROLES } from "../../services/constants";
import dashboardImg from "../../assets/images/home/dashboard.png";
import workspaceImg from "../../assets/images/home/workspace.png";
import buildingsdImg from "../../assets/images/home/buildings.svg";
import dgtlPwrPortal from "../../assets/images/home/dgtl-pwr-portal.svg";
import craasDashboard from "../../assets/images/home/craas-dashboard.svg";
import { selectSystemType } from "../../redux/selectSystem/actions";
import { WORKFLOW_SYSTEM, PRAAS_SYSTEM } from "../../constants";
import { FlipCard } from "../../components/flipCard";

const useStyles = makeStyles(() => ({
  root: {
    padding: `${theme.spacing(10)}px ${theme.spacing(8)}px`,
  },
  title: {
    marginBottom: theme.spacing(12),
    fontWeight: 400,
  },
  subTitle: {
    position: "absolute",
    top: 265,
    color: COLORS.davisGrey,
  },
  enableSubTitle: {
    position: "absolute",
    top: 265,
  },
  img: {
    position: "absolute",
    bottom: 150,
    opacity: 0.5,
  },
  topLine: {
    width: "100%",
    height: "12px",
    border: `1px solid ${COLORS.darks[0][10]}`,
    borderBottomLeftRadius: "0px",
    borderBottomRightRadius: "0px",
  },
  enableImg: {
    position: "absolute",
    bottom: 150,
  },
  card: {
    border: `1px solid ${COLORS.darks[0][10]}`,
    borderRadius: theme.spacing(1),
    height: 370,
    width: 240,
    position: "relative",
    marginRight: theme.spacing(1.5),
    marginLeft: theme.spacing(1.5),
  },
  enableCard: {
    border: `1px solid ${COLORS.darks[0][10]}`,
    borderRadius: theme.spacing(1),
    height: 370,
    width: 240,
    position: "relative",
    marginRight: theme.spacing(1.5),
    marginLeft: theme.spacing(1.5),
    cursor: "pointer",
    "&:hover": {
      boxShadow: `0px 10px 10px ${COLORS.purples[10]}`,
    },
  },
}));

var items = [
  {
    icon: dashboardImg,
    text: "PRaaS DASHBOARD",
    enable: true,
    id: "praas-dashboard-card",
    page: PAGES.dashboard.index,
    system_type: PRAAS_SYSTEM,
    styling_gradient: `linear-gradient(0deg, ${COLORS.purples[0]}, ${COLORS.purples[0]}),linear-gradient(0deg, rgba(33, 33, 33, 0.1), rgba(33, 33, 33, 0.1))`,
  },
  {
    icon: workspaceImg,
    text: "PRaaS WORKSPACE",
    enable: true,
    id: "praas-workspace-card",
    page: `${PAGES.workspace.index}/${PAGES.workspace.tasks}`,
    system_type: WORKFLOW_SYSTEM,
    styling_gradient: `linear-gradient(0deg, ${COLORS.purples[0]}, ${COLORS.purples[0]}),linear-gradient(0deg, rgba(33, 33, 33, 0.1), rgba(33, 33, 33, 0.1))`,
  },
  {
    icon: buildingsdImg,
    text: "PROPERTIES SCIENCE",
    enable: true,
    id: "praas-buildings",
    page: `${PAGES.buildings.index}`,
    styling_gradient: `linear-gradient(0deg, ${COLORS.purples[0]}, ${COLORS.purples[0]}),linear-gradient(0deg, rgba(33, 33, 33, 0.1), rgba(33, 33, 33, 0.1))`,
  },
  {
    icon: dgtlPwrPortal,
    text: "DIGITAL POWER PORTAL",
    enable: false,
    id: "carboonzero-digital-power-portal",
    styling_gradient: `linear-gradient(0deg, rgba(33, 33, 33, 0.1), rgba(33, 33, 33, 0.1)), linear-gradient(0deg, ${COLORS.rebeccaPurple}, ${COLORS.rebeccaPurple}), linear-gradient(0deg, ${COLORS.silverSand[100]}, ${COLORS.silverSand[100]})`,
  },
  {
    icon: craasDashboard,
    text: "CRaaS DASHBOARD",
    enable: false,
    id: "carboonzero-craas-dashboard",
    styling_gradient: `linear-gradient(0deg, rgba(33, 33, 33, 0.1), rgba(33, 33, 33, 0.1)), linear-gradient(0deg, ${COLORS.bluePalatinate}, ${COLORS.bluePalatinate})`,
  },
];

export const Home = ({ preliminaryMode = false }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { cluster } = useSelector((state) => state.selectCSC);

  if (preliminaryMode === true) {
    items = items.map((e) => ({
      ...e,
      enable: e.id === "praas-workspace-card",
      page: null,
    }));
  }

  useEffect(() => {
    if (cluster.user_role === USER_ROLES.bop) {
      navigate(`${PAGES.workspace.index}/${PAGES.workspace.tasks}`);
      dispatch(selectSystemType(WORKFLOW_SYSTEM));
    }
  }, [cluster, navigate]);

  const handleClick = (item) => {
    if (item.page) {
      if (item.system_type) dispatch(selectSystemType(item.system_type));
      if (
        item.page === PAGES.workspace.index &&
        cluster.user_role === USER_ROLES.super_pm &&
        !cluster.has_portfolio
      ) {
        navigate(
          `${PAGES.workspace.index}${PAGES.workspace.settings.index}/${PAGES.workspace.settings.portfolioRegistration}`,
        );
      } else {
        navigate(item.page);
      }
    }
  };

  return (
    <div className={classes.root}>
      <Typography className={classes.title} variant="h1" align="center">
        We&apos;re happy you&apos;re here!
      </Typography>
      <Grid container justifyContent="center" style={{ flexWrap: "nowrap" }}>
        {items.map((item, index) => (
          <Grid item key={index} id={item.id}>
            {preliminaryMode && item.enable ? (
              <FlipCard item={item} />
            ) : (
              <Grid
                className={item.enable ? classes.enableCard : classes.card}
                container
                direction="column"
                alignItems="center"
                onClick={() => handleClick(item)}
              >
                <Card className={classes.topLine} style={{ background: item.styling_gradient }}>
                  <CardContent />
                </Card>
                <img
                  className={item.enable ? classes.enableImg : classes.img}
                  src={item.icon}
                  alt="icon"
                />
                <div className={item.enable ? classes.enableSubTitle : classes.subTitle}>
                  <Typography key={index} variant="subtitle1" align="center">
                    {item.text}
                  </Typography>
                </div>
              </Grid>
            )}
          </Grid>
        ))}
      </Grid>
    </div>
  );
};